import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import type { TenantInfo, SaasModule, TenantModuleRow } from '../page';

interface Props {
  tenant: TenantInfo;
  modules: SaasModule[];
  planSet: Set<string>;
  tmMap: Record<string, TenantModuleRow>;
  planPrice: number;
  monthlyAddons: number;
  addonModules: SaasModule[];
  onTenantChange: (t: TenantInfo) => void;
  onReload: () => void;
}

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const PLAN_STYLES: Record<string, { badge: string; text: string; border: string; bg: string }> = {
  free:       { badge: 'bg-slate-100 text-slate-500',    text: 'text-slate-500',   border: 'border-slate-200',   bg: 'bg-slate-50' },
  pro:        { badge: 'bg-amber-50 text-amber-700',     text: 'text-amber-700',   border: 'border-amber-200',   bg: 'bg-amber-50' },
  enterprise: { badge: 'bg-emerald-50 text-emerald-700', text: 'text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50' },
};

const MODULE_ICONS: Record<string, string> = {
  clients: 'ri-user-3-line', contracts: 'ri-file-text-line', payments: 'ri-bank-card-line',
  jobs: 'ri-briefcase-line', public_website: 'ri-global-line', client_portal: 'ri-user-shared-line',
  basic_branding: 'ri-palette-line', digital_invitations: 'ri-mail-send-line', rsvp: 'ri-calendar-check-line',
  media_packages: 'ri-folder-image-line', dvd_online: 'ri-disc-line', photo_selection: 'ri-image-line',
  final_deliveries: 'ri-send-plane-line', ai_sales_assistant: 'ri-robot-line', appointments: 'ri-calendar-line',
  whatsapp_notifications: 'ri-whatsapp-line', email_automations: 'ri-mail-settings-line',
  custom_domain: 'ri-earth-line', extra_storage: 'ri-hard-drive-line',
};

export default function BillingTab({
  tenant, modules, planSet, tmMap, planPrice, monthlyAddons, addonModules,
  onTenantChange, onReload,
}: Props) {
  const ps = PLAN_STYLES[tenant.plan_code] ?? PLAN_STYLES.free;
  const total = planPrice + monthlyAddons;

  const getAddonPrice = (mod: SaasModule): number =>
    tmMap[mod.id] ? Number(tmMap[mod.id].price_monthly) : mod.price_monthly;

  const handleChangePlan = async () => {
    const { data: plans } = await supabase.from('saas_plans').select('id, name, price_monthly').order('price_monthly');
    const opts = (plans ?? []).reduce<Record<string, string>>((acc, p) => {
      acc[p.id] = `${p.name} — $${p.price_monthly}/mo`;
      return acc;
    }, {});
    const { value: newPlan } = await Swal.fire({
      title: 'Cambiar plan', input: 'select', inputOptions: opts,
      inputValue: tenant.plan_code, showCancelButton: true,
      confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newPlan || newPlan === tenant.plan_code) return;
    const { data: planFull } = await supabase.from('saas_plans').select('max_users, max_contracts, storage_mb').eq('id', newPlan).maybeSingle();
    const { error } = await supabase.from('saas_tenants').update({
      plan_code: newPlan,
      max_users: planFull?.max_users ?? tenant.max_users,
      max_active_contracts: planFull?.max_contracts ?? tenant.max_active_contracts,
      storage_quota_mb: planFull?.storage_mb ?? tenant.storage_quota_mb,
      updated_at: new Date().toISOString(),
    }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, plan_code: newPlan });
    Swal.fire({ title: 'Plan actualizado', icon: 'success', timer: 1400, showConfirmButton: false, ...SW });
    onReload();
  };

  const includedModules = modules.filter((m) => planSet.has(m.id));

  return (
    <div className="space-y-5 max-w-3xl">

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-2xl border ${ps.border} ${ps.bg} p-5`}>
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Plan base</p>
          <p className={`text-3xl font-bold ${ps.text}`}>
            ${planPrice}
            <span className="text-slate-400 text-sm font-normal">/mes</span>
          </p>
          <div className="mt-3 flex items-center justify-between">
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${ps.badge}`}>{tenant.plan_code}</span>
            <button
              onClick={handleChangePlan}
              className="text-xs text-slate-500 hover:text-blue-600 cursor-pointer transition-colors"
            >
              Cambiar →
            </button>
          </div>
        </div>

        <div className="bg-white border border-amber-200 rounded-2xl p-5">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Add-ons</p>
          <p className="text-3xl font-bold text-amber-600">
            ${monthlyAddons}
            <span className="text-slate-400 text-sm font-normal">/mes</span>
          </p>
          <p className="text-slate-400 text-xs mt-3">{addonModules.length} add-on{addonModules.length !== 1 ? 's' : ''} activos</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Total estimado</p>
          <p className="text-3xl font-bold text-slate-900">
            ${total}
            <span className="text-slate-400 text-sm font-normal">/mes</span>
          </p>
          <p className="text-slate-400 text-xs mt-3">MRR de este tenant</p>
        </div>
      </div>

      {/* Visual bar */}
      {total > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Composición del ingreso mensual</span>
            <span>${total}/mes</span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden flex">
            <div
              className={`h-full ${ps.bg.replace('bg-', 'bg-').replace('-50', '-300')} transition-all`}
              style={{ width: `${total > 0 ? (planPrice / total) * 100 : 0}%` }}
            />
            {monthlyAddons > 0 && (
              <div
                className="h-full bg-amber-300 transition-all"
                style={{ width: `${(monthlyAddons / total) * 100}%` }}
              />
            )}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${ps.bg.replace('-50', '-300')}`}></span>
              <span className="text-slate-500 text-xs">Plan ({planPrice > 0 ? Math.round((planPrice / total) * 100) : 0}%)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-300"></span>
              <span className="text-slate-500 text-xs">Add-ons ({monthlyAddons > 0 ? Math.round((monthlyAddons / total) * 100) : 0}%)</span>
            </div>
          </div>
        </div>
      )}

      {/* Add-ons detail */}
      {addonModules.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Add-ons activos</p>
            <p className="text-amber-600 text-xs font-semibold">${monthlyAddons}/mes total</p>
          </div>
          {addonModules.map((mod, idx) => {
            const price = getAddonPrice(mod);
            const icon  = MODULE_ICONS[mod.id] ?? 'ri-puzzle-line';
            return (
              <div key={mod.id} className={`flex items-center justify-between px-5 py-3.5 ${idx < addonModules.length - 1 ? 'border-b border-slate-100' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 shrink-0">
                    <i className={`${icon} text-sm`}></i>
                  </span>
                  <p className="text-slate-700 text-sm font-medium">{mod.name}</p>
                </div>
                <p className="text-amber-600 text-sm font-bold">${price}<span className="text-slate-400 text-xs font-normal">/mes</span></p>
              </div>
            );
          })}
        </div>
      )}

      {/* Included in plan */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Incluido en el plan</p>
          <p className="text-slate-400 text-xs">{includedModules.length} módulos · $0 extra</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-0">
          {includedModules.map((mod, idx) => {
            const icon = MODULE_ICONS[mod.id] ?? 'ri-apps-line';
            return (
              <div key={mod.id} className={`flex items-center gap-2.5 px-4 py-3 ${idx < includedModules.length - 1 ? 'border-b border-slate-100' : ''}`}>
                <span className="w-6 h-6 flex items-center justify-center text-emerald-500 shrink-0">
                  <i className={`${icon} text-xs`}></i>
                </span>
                <span className="text-slate-500 text-xs">{mod.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
